import { useCallback, useEffect, useState } from 'react';
import { ChipProps } from '@mui/material';

import { TextFieldProps } from '../../TextField';
import { GetChipProps, OnChange, RenderInput, RenderTags } from '../types';
import { checkPlaceholder } from '../utils/checkPlaceholder';
import { getOffset } from '../utils/getOffset';
import { isValueEmpty } from '../utils/isValueEmpty';
import { renderChips } from '../utils/renderChips';
import { renderTextField } from '../utils/renderTextField';

export interface UseAutocompleteProps<Value> {
  chipProps: Partial<ChipProps>;
  defaultValue?: Value[];
  getChipProps?: GetChipProps<Value>;
  handleChange?: (value: Value[]) => void;
  hasCounter?: boolean;
  initialValue?: Value[];
  maxLength?: number;
  textFieldProps?: Partial<TextFieldProps>;
}

export interface UseAutocompleteResult<Value> {
  handleAddValue: (value: Value[]) => void;
  onChange: OnChange<Value>;
  renderInput: RenderInput<Value>;
  renderTags: RenderTags<Value>;
  value: Value[];
}

export const useAutocomplete = <Value>({
  chipProps,
  defaultValue,
  getChipProps,
  handleChange,
  hasCounter,
  initialValue,
  maxLength,
  textFieldProps,
}: UseAutocompleteProps<Value>): UseAutocompleteResult<Value> => {
  const [value, setValue] = useState(initialValue ?? []);
  const [hasPlaceholder, setHasPlaceholder] = useState(
    checkPlaceholder(value, defaultValue),
  );
  const [offset, setOffset] = useState(getOffset(value, defaultValue));

  const handleSetValue = useCallback(
    (valueToSet: Value[]) => {
      const newValue = valueToSet
        .slice(0, maxLength)
        .map(item => (item as string)?.trim() as Value)
        .filter(Boolean);

      setHasPlaceholder(isValueEmpty(newValue));
      setOffset(newValue.length);
      setValue(newValue);

      handleChange?.(newValue);
    },
    [handleChange, maxLength],
  );

  const handleAddValue = useCallback(
    (valueToSet: Value[]) => handleSetValue([...value, ...valueToSet]),
    [handleSetValue, value],
  );

  const renderInput = useCallback<RenderInput<Value>>(
    autocompleteRenderInputParams =>
      renderTextField({
        autocompleteRenderInputParams,
        hasCounter,
        hasPlaceholder,
        maxLength,
        offset,
        textFieldProps,
      }),
    [hasCounter, hasPlaceholder, maxLength, offset, textFieldProps],
  );

  const renderTags = useCallback<RenderTags<Value>>(
    (currentValue, getTagProps) =>
      renderChips({
        chipProps,
        getChipProps,
        getTagProps,
        value: currentValue,
      }),
    [chipProps, getChipProps],
  );

  const onChange = useCallback<OnChange<Value>>(
    (_event, rawValue) => handleSetValue(rawValue as Value[]),
    [handleSetValue],
  );

  useEffect(() => setValue(initialValue ?? []), [initialValue]);
  useEffect(() => {
    setHasPlaceholder(isValueEmpty(value));
    setOffset(getOffset(value));
  }, [value]);

  return { handleAddValue, renderInput, renderTags, onChange, value };
};
